/**
 * NKS's words for where a person is in their relationship with the dealership.
 *
 * The list rendered `row.lifecycleStage` straight from the API, so the screen
 * read CUSTOMER and PROSPECT in enum caps while every other chip in the app was
 * sentence case. An enum value is a storage detail; it is not a label, and the
 * reference's copy rules (§7) are explicit that these are written the way staff
 * say them.
 *
 * Mirrors `enum LifecycleStage` in prisma/schema.prisma.
 */
export const LIFECYCLE_STAGE_LABEL: Record<string, string> = {
  LEAD: 'Lead',
  PROSPECT: 'Prospect',
  CUSTOMER: 'Customer',
  OWNER: 'Owner',
  LAPSED: 'Lapsed',
};

/**
 * Falls back to the raw value rather than rendering an empty chip.
 *
 * `lifecycleStage` crosses the wire as a string (see api.ts), so a newer API
 * can send a stage this bundle has never heard of. Showing the code is ugly;
 * showing nothing loses information the user needs.
 */
export function stageLabel(stage: string): string {
  return LIFECYCLE_STAGE_LABEL[stage] ?? stage;
}

/**
 * The tone a stage chip carries.
 *
 * OWNER is the one worth colouring — someone who has actually taken delivery —
 * and LAPSED is the one worth flagging. The rest stay neutral so the colour in
 * this column means something.
 */
export function stageTone(stage: string): 'default' | 'success' | 'warning' {
  if (stage === 'OWNER') return 'success';
  if (stage === 'LAPSED') return 'warning';
  return 'default';
}

/**
 * INDIVIDUAL / COMPANY, written the way a counter clerk would say it.
 *
 * Same reasoning as the stage labels: this sat next to the stage chip on the
 * customer header and read in enum caps.
 */
export const CUSTOMER_TYPE_LABEL: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  COMPANY: 'Company',
};

export function customerTypeLabel(type: string): string {
  return CUSTOMER_TYPE_LABEL[type] ?? type;
}
